import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useUnit } from "effector-react";
import { Fragment, Suspense, lazy, useState } from "react";

import images from "../constants/images";
import { $gameConfig } from "../store";
import type {
  ResourceObjects,
  TokenValleyBlockType,
} from "../types/tokenValley";
import { formatTypesName } from "../utils/formatTypesName";
import { getBuildingDescription } from "../utils/getBuildingDescription";
import { getResourceGatheringIconResourcesTypes } from "../utils/getResourceGatheringIcon";
import IconBlock from "./IconBlock";

const Dialog = lazy(() => import("@mui/material/Dialog"));

interface PropsTypes {
  activeBlock: TokenValleyBlockType | null;
}
const InfoBlockButton = ({ activeBlock }: PropsTypes) => {
  const [open, setOpen] = useState(false);
  const [gameConfig] = useUnit([$gameConfig]);

  const configTypeBlock =
    activeBlock?.block_type !== undefined
      ? gameConfig?.buildings_spec[activeBlock.block_type].production_levels[1]
      : undefined;
  type ConversionItem = {
    id: number;
    title: string;
    value: string | number;
  };

  const conversionData: ConversionItem[] = [
    configTypeBlock?.produce_speed_ms != null && {
      id: 1,
      title: "Speed",
      value: `${configTypeBlock.produce_speed_ms / 1000} s`,
    },
    configTypeBlock?.consume &&
      Object.keys(configTypeBlock.consume).length > 0 && {
        id: 2,
        title: "Consume",
        value: Object.values(configTypeBlock.consume)[0],
      },
    configTypeBlock?.produce &&
      Object.keys(configTypeBlock.produce).length > 0 && {
        id: 3,
        title: "Produce",
        value: Object.values(configTypeBlock.produce)[0],
      },
  ].filter(Boolean) as ConversionItem[];

  return (
    <Fragment>
      <Button
        variant="darkBlue"
        onClick={() => {
          setOpen(true);
        }}
      >
        <img
          src={images.icons.QuestionMark}
          alt=""
          style={{ marginRight: "5px", marginBottom: "2px" }}
        />
        Info
      </Button>

      <Suspense fallback={<CircularProgress size={40} />}>
        <Dialog
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          PaperProps={{
            sx: {
              minWidth: "90vw",
              border: "2px solid",
              borderRadius: "15px",
              bgcolor: "primary.bgModalBuild",
              borderColor: "secondary.borderInfoDialog",
            },
          }}
        >
          <Box
            height={"55px"}
            display={"flex"}
            alignItems={"center"}
            position={"relative"}
            bgcolor={"primary.main"}
            justifyContent={"center"}
            borderBottom={"2px solid"}
            borderColor={"primary.borderBlue"}
          >
            <Typography
              color="white"
              fontSize={"20px"}
              sx={{
                textShadow: `
                               -2.2px -2.2px 1px #344871,
                                2.2px -2.2px 1px #344871,
                                -2.2px 2.2px 1px #344871,
                                2.2px 2.2px 1px #344871
                              `,
              }}
            >
              Building
            </Typography>
            <Button
              onClick={() => setOpen(false)}
              variant="close"
              sx={{
                width: "37px",
                height: "37px",
                position: "absolute",
                right: "10px",
                borderRadius: "11px",
              }}
            >
              <img
                src={images.icons.XClose}
                alt=""
                style={{ width: "16px", height: "19px" }}
              />
            </Button>
          </Box>
          <Box p={2} gap={1} display={"flex"} flexDirection={"column"}>
            <Box display={"flex"} gap={1}>
              <Box
                width={"110px"}
                height={"110px"}
                display={"flex"}
                minWidth={"110px"}
                minHeight={"110px"}
                bgcolor={"#B9CFFF"}
                overflow={"hidden"}
                border={"2px solid"}
                borderRadius={"6px"}
                alignItems={"center"}
                borderColor={"#3C548C"}
                justifyContent={"center"}
                sx={{
                  backgroundImage: `url(${images.icons.BgLine})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                {activeBlock && (
                  <IconBlock
                    blockType={activeBlock.block_type}
                    bigSize={true}
                  />
                )}
              </Box>
              <Box
                p={1}
                overflow={"auto"}
                maxHeight={"110px"}
                borderRadius={"6px"}
                bgcolor={"secondary.bgDialogBuild"}
                sx={{
                  "&::-webkit-scrollbar": { display: "none" },
                }}
                border={"2px solid"}
                borderColor={"secondary.bgDialogBuild"}
              >
                <Typography
                  color="white"
                  fontSize={"16px"}
                  sx={{
                    textShadow: `
                               -1.5px -1.5px 1.5px #344871,
                                1.5px -1.5px 1.5px #344871,
                                -1.5px 1.5px 1.5px #344871,
                                1.5px 1.5px 1.5px #344871
                              `,
                  }}
                >
                  {activeBlock?.block_type
                    ? formatTypesName(activeBlock.block_type)
                    : "Building"}
                </Typography>
                <Typography
                  fontWeight={900}
                  fontSize={"10px"}
                  fontFamily={"Red Hat Display"}
                >
                  {getBuildingDescription(activeBlock?.block_type || "")}
                </Typography>
              </Box>
            </Box>
            {Object.keys(activeBlock?.resource_stock || {}).length !== 0 && (
              <Box
                p={1}
                gap={1}
                display={"flex"}
                borderRadius={"6px"}
                flexDirection={"column"}
                bgcolor={"secondary.bgDialogBuild"}
              >
                <Box
                  p={1}
                  gap={1}
                  width={"100%"}
                  display={"flex"}
                  bgcolor={"#DDECFF"}
                  borderRadius={"6px"}
                >
                  <Box
                    width={"45px"}
                    height={"45px"}
                    display={"flex"}
                    position={"relative"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    sx={{
                      backgroundImage: `url(${images.lab.bg})`,
                      backgroundSize: "cover",
                    }}
                  >
                    <img
                      style={{ width: "33px", height: "33px" }}
                      src={
                        getResourceGatheringIconResourcesTypes(
                          Object.keys(
                            activeBlock?.resource_stock ?? {},
                          )[0] as ResourceObjects,
                        ) || ""
                      }
                      alt={""}
                    />
                  </Box>
                  {activeBlock &&
                  activeBlock?.block_time_ready < new Date().getTime() ? (
                    <Box display={"flex"} flexDirection={"column"}>
                      <Typography
                        fontSize={"10px"}
                        fontFamily={"Red Hat Display"}
                        color="primary.textResearchLab"
                      >
                        Collected :{" "}
                        {formatTypesName(
                          Object.keys(activeBlock?.resource_stock ?? {})[0],
                        )}
                      </Typography>
                      <Typography
                        color="white"
                        fontWeight={900}
                        fontSize={"16px"}
                        fontFamily={"Red Hat Display"}
                        sx={{
                          textShadow: `
                               -1.3px -1.3px 1px #344871,
                                1.3px -1.3px 1px #344871,
                                -1.3px 1.3px 1px #344871,
                                1.3px 1.3px 1px #344871
                              `,
                        }}
                      >
                        {Object.values(activeBlock?.resource_stock ?? {})[0]}
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Typography
                        fontSize={"14px"}
                        fontFamily={"Red Hat Display"}
                        color="primary.textResearchLab"
                      >
                        Extraction resource : <br />
                        {formatTypesName(
                          Object.keys(activeBlock?.resource_stock ?? {})[0],
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box
                  borderRadius={"6px"}
                  bgcolor={"#DDECFF"}
                  overflow={"hidden"}
                >
                  <Box
                    width={"100%"}
                    height={"21px"}
                    display={"flex"}
                    bgcolor={"#9FBCE1"}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <Typography
                      fontSize={"12px"}
                      color="primary.textResearchLab"
                    >
                      Conversion
                    </Typography>
                  </Box>
                  <Box p={1} display={"flex"} gap={1}>
                    {configTypeBlock?.consume && (
                      <Box
                        width={"53px"}
                        height={"53px"}
                        bgcolor={"#C6D9F1"}
                        position={"relative"}
                        borderRadius={"1.5px"}
                      >
                        <Box
                          top={"5px"}
                          left={"5px"}
                          width={"27px"}
                          height={"27px"}
                          display={"flex"}
                          position={"absolute"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          sx={{
                            backgroundImage: `url(${images.lab.bg})`,
                            backgroundSize: "cover",
                          }}
                        >
                          <img
                            style={{ width: "19px", height: "19px" }}
                            src={
                              getResourceGatheringIconResourcesTypes(
                                Object.keys(
                                  configTypeBlock?.consume ?? {},
                                )[0] as ResourceObjects,
                              ) || undefined
                            }
                            alt={""}
                          />
                        </Box>
                        <Box
                          right={"5px"}
                          bottom={"5px"}
                          width={"27px"}
                          height={"27px"}
                          display={"flex"}
                          alignItems={"center"}
                          position={"absolute"}
                          justifyContent={"center"}
                          sx={{
                            backgroundImage: `url(${images.lab.bg})`,
                            backgroundSize: "cover",
                          }}
                        >
                          <img
                            style={{ width: "19px", height: "19px" }}
                            src={
                              getResourceGatheringIconResourcesTypes(
                                Object.keys(
                                  configTypeBlock?.produce ?? {},
                                )[0] as ResourceObjects,
                              ) || undefined
                            }
                            alt={""}
                          />
                        </Box>
                        <img
                          src={images.icons.ArrowConversion}
                          alt=""
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "10px",
                          }}
                        />
                      </Box>
                    )}
                    <Box width={"100%"}>
                      {conversionData.map((item) => (
                        <Box
                          key={item.id}
                          display={"flex"}
                          justifyContent={"space-between"}
                          alignItems={"center"}
                        >
                          <Typography
                            fontSize={"10px"}
                            color="primary.textResearchLab"
                          >
                            {item.title}:
                          </Typography>
                          <Typography
                            color={"white"}
                            fontSize={"12px"}
                            sx={{
                              textShadow: `
                                    -1.2px -1.2px 1.2px #344871,
                                     1.2px -1.2px 1.2px #344871,
                                     -1.2px 1.2px 1.2px #344871,
                                     1.2px 1.2px 1.2px #344871
                                   `,
                            }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Dialog>
      </Suspense>
    </Fragment>
  );
};

export default InfoBlockButton;
